import {logger} from 'yougile-util'
import {User} from 'yougile-model'
import {DirectMessages} from '@client/direct-messages'
import {Sync} from '@client/sync'
import {UserSettings} from '@client/user-settings'
import {FilePreview} from '@common/file-preview'
import {globals} from '@extensions/globals-base'
import {ChatMessageUtils} from '../chat-message-utils'
import {ChatUtils} from '../chat-utils'
import {ChatProxy} from './chat-proxy'
import {UserProxy} from './user-proxy'

export type PersonalChatProxy = ChatProxy & UserProxy & {starred: boolean}

export const PersonalChatProxy = {
  create: (user: User, companion: User): PersonalChatProxy => {
    if (!companion) {
      logger.warn('No companion specified')
      return undefined
    }
    const hub = DirectMessages.requestChatWith(companion.id)
    const userAvatarProxy = UserProxy.create(companion)

    const lastMessage = hub && hub.chat.lastMessage()
    const lastMessageFromMe =
      hub &&
      hub.chat.lastMessage({
        userId: globals.currentUserId
      })

    type ChatProxyPart = Pick<
      PersonalChatProxy,
      | 'newMessagesAmount'
      | 'getNewMentionsAmount'
      | 'lastMessageText'
      | 'lastMessageAuthor'
      | 'lastMessageTimestamp'
      | 'lastMessageId'
    >

    let chatPart: ChatProxyPart = {
      newMessagesAmount: 0,
      getNewMentionsAmount: () => 0,
      lastMessageText: undefined,
      lastMessageAuthor: undefined,
      lastMessageTimestamp: undefined,
      lastMessageId: undefined
    }

    if (lastMessage) {
      const author = ChatMessageUtils.author(lastMessage, hub)
      if (author) {
        chatPart = {
          newMessagesAmount: hub.chat.newMessagesFor(user.id, user.timestamp),
          getNewMentionsAmount: () => 0,
          lastMessageText: FilePreview.encodedToFilenames(lastMessage.text),
          lastMessageAuthor: ChatUtils.getUserRealNameForDisplay(
            author.id,
            hub
          ),
          lastMessageTimestamp: lastMessage.timestamp,
          lastMessageId: lastMessage.id
        }
      }
    }

    return {
      chatId: hub ? hub.id : userAvatarProxy.userId,
      messagesCount: hub.chat.messagesCount,
      createdTimestamp: user.timestamp,
      chatType: 'user',
      chatName: userAvatarProxy.userName,
      locationPath: hub ? ChatUtils.getChatLocation(hub) : '',
      starred: UserSettings.isStarred(hub),
      markImportant: hub?.data?.markImportantMap?.[globals.currentUserId],
      markAsSeen: () => hub && ChatUtils.markAsSeen(hub),
      lastMessageFromMeTimestamp: lastMessageFromMe?.timestamp,
      ...userAvatarProxy,
      ...chatPart
    }
  },

  createForCurrentUser: (companion: User): PersonalChatProxy =>
    PersonalChatProxy.create(Sync.getCurrentUser(), companion)
}
